/* eslint-disable max-len */
/* eslint-disable no-nested-ternary */
import { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import {
  startGameCounter,
  stopGameCounter,
  resolveGame,
  resolveGameTeams,
  resetScore,
  setPointsHistory,
  incrementQueueWaiting,
  addMatchToHistory,
} from 'redux/slices';
import { EndGameDialog } from './EndGameDialog';
import { ConfirmationModal } from './ConfirmationModal';

export function StartGameBtn() {
  const {
    gameStarted, scoreA, scoreB, playersPerTeam, time, maxRallySeconds,
  } = useSelector((state) => state.game);
  const players = useSelector((state) => state.players);
  const teams = useSelector((state) => state.teams);
  const dispatch = useDispatch();

  const [endDialogOpen, setEndDialogOpen] = useState(false);

  const teamA = players.filter((p) => p.teamId === 1);
  const teamB = players.filter((p) => p.teamId === 2);

  const teamsHavePlayers = teamA.length === playersPerTeam && teamB.length === playersPerTeam;
  const scoreIsZero = scoreA === 0 && scoreB === 0;
  // Empate (inclui 0x0) não tem vencedor — antes o Time B era declarado vencedor.
  const scoreIsTied = scoreA === scoreB;

  const teamThatWinLastGame = teams.find((t) => t.id === 1)?.winLastGame ? 'A' : 'B';

  // Vencedor calculado a partir do placar no momento do encerramento.
  const WINNER = scoreA > scoreB ? 1 : 2;
  const LOSER = scoreA > scoreB ? 2 : 1;
  const winnerName = teams.find((t) => t.id === WINNER)?.name || `Time ${WINNER === 1 ? 'A' : 'B'}`;

  const handleStartGame = () => {
    // A espera da fila incrementa ao iniciar a partida (não mais ao encerrar).
    dispatch(incrementQueueWaiting());
    dispatch(startGameCounter());
  };

  const getPlayerPoints = (p) => p.totalPoints ?? (p.wins - p.losses);
  const snapshotTeam = (teamId, teamPlayers) => ({
    name: teams.find((t) => t.id === teamId)?.name || `Time ${teamId === 1 ? 'A' : 'B'}`,
    points: teamPlayers.reduce((sum, p) => sum + getPlayerPoints(p), 0),
    players: teamPlayers.map((p) => ({ playerName: p.playerName, gender: p.gender })),
  });

  const handleConfirmEnd = (mode) => {
    const params = {
      winnerTeamId: WINNER,
      loserTeamId: LOSER,
      ...mode,
    };

    // Registra a partida no histórico do dia (antes de resolver os times).
    dispatch(addMatchToHistory({
      playedAt: Date.now(),
      scoreA,
      scoreB,
      winner: WINNER === 1 ? 'A' : 'B',
      durationSeconds: (time.minutes * 60) + time.seconds,
      maxRallySeconds,
      teamA: snapshotTeam(1, teamA),
      teamB: snapshotTeam(2, teamB),
    }));

    dispatch(stopGameCounter());
    dispatch(resolveGameTeams(params));
    dispatch(resolveGame(params));

    // Define quem começa com a bola no próximo jogo: o time que ficou em quadra.
    let stayingTeamId = null;
    if (mode.winnerStays) stayingTeamId = WINNER;
    else if (mode.loserStays) stayingTeamId = LOSER;
    dispatch(setPointsHistory(stayingTeamId ? [stayingTeamId === 1 ? 'A' : 'B'] : []));

    setEndDialogOpen(false);
  };

  return (
    <Box sx={{ margin: '20px auto', width: 300 }} mt={1} display="flex" justifyContent="center" flexDirection="column">
      <Typography variant="h6" align="center" mb={3}>
        {scoreIsZero ? `A bola começa com o time ${teamThatWinLastGame} !` : ''}
      </Typography>
      {
        !gameStarted
          ? <Button disabled={!teamsHavePlayers} variant="contained" color="secondary" onClick={handleStartGame}>Começar Jogo</Button>
          : <Button disabled={scoreIsTied} variant="contained" onClick={() => setEndDialogOpen(true)}>Finalizar Jogo</Button>
      }
      {gameStarted && scoreIsTied && !scoreIsZero && (
        <Typography variant="caption" align="center" sx={{ mt: 0.5 }}>
          Placar empatado — marque o ponto de desempate para finalizar.
        </Typography>
      )}
      <br />
      <ConfirmationModal
        title="Resetar placar"
        description="Tem certeza que deseja zerar o placar desta partida?"
        triggerLabel="Resetar Placar"
        disabled={scoreIsZero}
        handleConfirm={() => dispatch(resetScore())}
        triggerConfig={{ variant: 'contained', color: 'error' }}
      />

      <EndGameDialog
        open={endDialogOpen}
        onClose={() => setEndDialogOpen(false)}
        winnerName={winnerName}
        onConfirm={handleConfirmEnd}
      />
    </Box>
  );
}
